import { ReferencedEntry } from 'contentful';
import { stringify } from 'qs';

import { ExternalLink } from 'src/api/contentful/contentTypes.cloverdotcom';
import { MAIN_LEAD_GEN_FORM } from 'src/components/App/Routes/Routes';
import { FlatEntry } from 'src/services/cms/flattenContentful';
import { setCookie } from 'src/utils/Cookies';
import { get, getField, validate } from './EntryUtil';

import { Environment } from './Environment';
import { getUrlFromFlattenedOrPlainEntry } from './Urls';

export const PDP_REDIRECT_URL = MAIN_LEAD_GEN_FORM;

// An array of RegExp patterns to match specific paths
const SALES_REPLACE_PATH_PATTERNS = [/shop\/\w+/, /no-hardware\/?/];

/**
 * Builds regular expression of a url using SALES_REPLACE_PATH_PATTERNS
 * @returns regular expression object
 */
function buildRegex () {
  const replacePathSources = SALES_REPLACE_PATH_PATTERNS.map(pattern => pattern.source);

  // Matches URL, supporting http, https, relative and absolute paths
  const replaceURLPattern = `(http[s]?:\/\/)?([^\/\s]|~)?\/?`;

  // Combines URL and path patterns into full RegExp
  const replacePattern = `${replaceURLPattern}(${replacePathSources.join('|')})`;
  return new RegExp(replacePattern, 'gi');
}

/**
 * Function that evaluates if the entry (url/slug) matches with specified patterns and if the user is a merchant
 * @param entry The plain Contentful entry, flatten Contentful entry or slug string
 * @returns Boolean
 */
export function shouldRedirectExistingMerchant(
  entry?: string | ReferencedEntry<ExternalLink> | FlatEntry<ExternalLink>
): boolean {
  const url = getUrlFromFlattenedOrPlainEntry(entry);
  const regexExp = buildRegex();
  const regExpMatches = regexExp.test(url);

  return Environment.current.isExistingMerchant && regExpMatches;
}

export function setExistingMerchantCookieWithSubDomains() {
  if (!Environment.current.isExistingMerchant) return;
  setCookie('isCloverMerchant', 'true', 365, true);
}

/**
 * Function that returns the url/slug mounted with params or the contact link if is existing merchant
 * @param entry The plain Contentful entry, flatten Contentful entry or slug string
 * @param queryParameters Optional array of objects that represents the query parameters
 * @returns String with url/slug mounted with params or the contact link
 */
export function getUrlWithParamsOrPdpRedirect(
  entry: string | ReferencedEntry<ExternalLink> | FlatEntry<ExternalLink>,
  queryParameters: Array<string | undefined> = []
): string {
  if (shouldRedirectExistingMerchant(entry)) {
    return PDP_REDIRECT_URL;
  }

  if (typeof entry === 'string') {
    return [
      `/${entry}`,
       stringify(queryParameters),
     ].filter(s => !!s).join('?');
  }

  let urlAndParams: Array<string | undefined>;

  const isPlainEntry = validate(entry as ReferencedEntry<ExternalLink>);

  if (isPlainEntry) {
    const flattenedEntry = entry as ReferencedEntry<ExternalLink>;
    urlAndParams = [
      getField(flattenedEntry, 'url'),
      stringify(getField(flattenedEntry, 'queryParameters')),
    ];
  } else {
    const entryAsPlain = entry as FlatEntry<ExternalLink>;
    urlAndParams = [
      get(entryAsPlain, 'url'),
      stringify(get(entryAsPlain, 'queryParameters')),
    ];
  }

  return urlAndParams.filter(s => !!s).join('?');
}
